import {
  Activity, BarChart2, Calendar, CheckSquare, ChevronDown, ChevronRight,
  ClipboardList, Database, Factory, FileText, LayoutDashboard, Package,
  Scale, Settings, TrendingUp, UserCog, Component, Layers, Table,
  Search, Clock, Star, Menu
} from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { cn } from '../../utils/tw';
import IconLink from './IconLink';
import { focusRing, hitArea } from '../../ui/utils/a11y';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface NavLink {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles: string[];
  group?: string;
  description?: string;
  frequency?: 'high' | 'medium' | 'low'; // Usage frequency
  end?: boolean; // Exact match for active state
}

// Main navigation structure with proper categorization
const mainLinks: NavLink[] = [
  // === DASHBOARDS (Most Accessed) ===
  {
    to: '/dashboards/v2',
    label: 'Dashboard V2',
    icon: <LayoutDashboard size={20} />,
    roles: ['admin', 'superadmin', 'misViewer'],
    group: 'Dashboards',
    frequency: 'high'
  },
  {
    to: '/dashboards/workflow',
    label: 'Workflow Dashboard',
    icon: <LayoutDashboard size={20} />,
    roles: ['user', 'editor', 'admin', 'superadmin', 'approver', 'misViewer', 'scheduler', 'auditor', 'manager', 'analyst'],
    group: 'Dashboards',
    frequency: 'high'
  },
  {
    to: '/dashboards/analytics',
    label: 'Analytics',
    icon: <TrendingUp size={20} />,
    roles: ['admin', 'superadmin', 'manager', 'analyst', 'misViewer'],
    group: 'Dashboards',
    frequency: 'high'
  },

  // === DAILY OPERATIONS (High Frequency) ===
  {
    to: '/entry',
    label: 'Data Entry',
    icon: <FileText size={20} />,
    roles: ['user', 'editor', 'admin', 'superadmin', 'approver'],
    group: 'Daily Operations',
    frequency: 'high'
  },
  {
    to: '/expenses/manage',
    label: 'Expense Management',
    icon: <Database size={20} />,
    roles: ['editor', 'admin', 'superadmin', 'approver', 'misViewer'],
    group: 'Daily Operations',
    frequency: 'high'
  },
  {
    to: '/approvals',
    label: 'Approvals',
    icon: <CheckSquare size={20} />,
    roles: ['admin', 'superadmin', 'approver'],
    group: 'Daily Operations',
    frequency: 'high'
  },

  // === PRODUCTION MANAGEMENT (New System) ===
  {
    to: '/production/manage',
    label: 'Daily Entry & Manage',
    icon: <ClipboardList size={20} />,
    roles: ['admin', 'editor', 'user'],
    group: 'Production Management',
    frequency: 'high',
    description: 'Enter daily production and view/edit entries'
  },
  {
    to: '/production/audit',
    label: 'Create Audit',
    icon: <Activity size={20} />,
    roles: ['admin', 'editor'],
    group: 'Production Management',
    frequency: 'medium',
    description: 'Perform production audit',
    end: true
  },
  {
    to: '/production/audit/history',
    label: 'Audit History',
    icon: <FileText size={20} />,
    roles: ['admin', 'superadmin', 'auditor'],
    group: 'Production Management',
    frequency: 'low',
    description: 'View past audits and unlock entries'
  },

  // === INVENTORY (Medium Frequency) ===
  {
    to: '/stock/usage',
    label: 'Stock Usage',
    icon: <ClipboardList size={20} />,
    roles: ['admin', 'editor', 'user'],
    group: 'Inventory',
    frequency: 'medium'
  },
  {
    to: '/stock/audit',
    label: 'Stock Audit',
    icon: <Scale size={20} />,
    roles: ['admin', 'editor'],
    group: 'Inventory',
    frequency: 'low'
  },

  // === REPORTS (Medium Frequency) ===
  {
    to: '/reports',
    label: 'Reports',
    icon: <BarChart2 size={20} />,
    roles: ['user', 'editor', 'admin', 'superadmin', 'approver', 'misViewer'],
    group: 'Reports',
    frequency: 'medium'
  },
  {
    to: '/scheduler',
    label: 'Report Scheduler',
    icon: <Calendar size={20} />,
    roles: ['admin', 'superadmin', 'scheduler'],
    group: 'Reports',
    frequency: 'low'
  },

  // === ADMINISTRATION (Low Frequency) ===
  {
    to: '/audit-logs',
    label: 'Audit Logs',
    icon: <FileText size={20} />,
    roles: ['admin', 'superadmin', 'auditor'],
    group: 'Administration',
    frequency: 'low'
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: <Settings size={20} />,
    roles: ['admin', 'superadmin'],
    group: 'Administration',
    frequency: 'low'
  },
  {
    to: '/admin/users',
    label: 'User Management',
    icon: <UserCog size={20} />,
    roles: ['admin', 'superadmin'],
    group: 'Administration',
    frequency: 'low'
  },

  // === V3 DASHBOARDS (In Development) ===
  {
    to: '/dashboards/workflow-v3',
    label: 'Workflow V3',
    icon: <Activity size={20} />,
    roles: ['user', 'editor', 'admin', 'superadmin', 'approver', 'misViewer', 'scheduler', 'auditor', 'manager', 'analyst'],
    group: 'V3 Dashboards',
    frequency: 'medium',
    description: 'New unified workflow dashboard with production integration'
  },
  {
    to: '/dashboards/analytics-v3',
    label: 'Analytics V3',
    icon: <BarChart2 size={20} />,
    roles: ['admin', 'superadmin', 'manager', 'analyst', 'misViewer', 'approver'],
    group: 'V3 Dashboards',
    frequency: 'medium',
    description: 'Unified analytics with Global → Type → Unit navigation'
  },
];

// Demo pages (only visible in development/preview)
const demoLinks: NavLink[] = import.meta.env.DEV || import.meta.env.VITE_IS_PREVIEW === 'true' ? [
  {
    to: '/demo/ui-kit',
    label: 'UI Kit',
    icon: <Component size={20} />,
    roles: ['admin', 'superadmin'],
    group: 'UI/UX Demos',
    frequency: 'low'
  },
  {
    to: '/demo/app-shell',
    label: 'App Shell',
    icon: <Layers size={20} />,
    roles: ['admin', 'superadmin'],
    group: 'UI/UX Demos',
    frequency: 'low'
  },
  {
    to: '/demo/form-system',
    label: 'Form System',
    icon: <FileText size={20} />,
    roles: ['admin', 'superadmin'],
    group: 'UI/UX Demos',
    frequency: 'low'
  },
  {
    to: '/demo/data-table',
    label: 'DataTable',
    icon: <Table size={20} />,
    roles: ['admin', 'superadmin'],
    group: 'UI/UX Demos',
    frequency: 'low'
  },
] : [];

const allLinks = [...mainLinks, ...demoLinks];

// Role-based navigation priority (what should appear first for each role)
const ROLE_NAVIGATION_PRIORITY = {
  'superadmin': ['Administration', 'Reports', 'Dashboards', 'Daily Operations', 'Production Management', 'Inventory'],
  'admin': ['Administration', 'Reports', 'Dashboards', 'Daily Operations', 'Production Management', 'Inventory'],
  'approver': ['Daily Operations', 'Dashboards', 'Reports', 'Administration', 'Production Management', 'Inventory'],
  'scheduler': ['Production Management', 'Inventory', 'Daily Operations', 'Dashboards', 'Reports', 'Administration'],
  'editor': ['Daily Operations', 'Dashboards', 'Production Management', 'Inventory', 'Reports', 'Administration'],
  'misViewer': ['Reports', 'Dashboards', 'Daily Operations', 'Production Management', 'Inventory', 'Administration'],
  'user': ['Daily Operations', 'Dashboards', 'Reports', 'Production Management', 'Inventory', 'Administration'],
} as const;

// Role hierarchy (highest to lowest)
const ROLE_HIERARCHY = ['superadmin', 'admin', 'approver', 'scheduler', 'editor', 'misViewer', 'user'];

// Group configuration with icons and default collapsed state
const groupConfig = {
  'Dashboards': { icon: <LayoutDashboard size={16} />, defaultOpen: true, priority: 1 },
  'Daily Operations': { icon: <FileText size={16} />, defaultOpen: true, priority: 2 },
  'Production Management': { icon: <Factory size={16} />, defaultOpen: true, priority: 3 },
  'Inventory': { icon: <Package size={16} />, defaultOpen: false, priority: 4 },
  'Reports': { icon: <BarChart2 size={16} />, defaultOpen: false, priority: 5 },
  'Administration': { icon: <Settings size={16} />, defaultOpen: false, priority: 6 },
  'V3 Dashboards': { icon: <Activity size={16} />, defaultOpen: true, priority: 6.5 },
  'UI/UX Demos': { icon: <Component size={16} />, defaultOpen: true, priority: 7 },
};

const AppSidebarEnhanced: React.FC<Props> = ({ open, onClose }) => {
  const { pathname } = useLocation();
  const { roles, user } = useAuthContext();
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // Get user's primary role
    const primaryRole = ROLE_HIERARCHY.find(role => roles?.includes(role)) || 'user';
    const rolePriority = ROLE_NAVIGATION_PRIORITY[primaryRole as keyof typeof ROLE_NAVIGATION_PRIORITY];

    // Open top 2 priority groups for the user's role by default
    return (Object.keys(groupConfig) as Array<keyof typeof groupConfig>).reduce((acc, key) => {
      const isTopPriority = rolePriority.slice(0, 2).includes(key as any);
      const isDemos = key === 'UI/UX Demos' && (import.meta.env.DEV || import.meta.env.VITE_IS_PREVIEW);
      acc[key] = !!(isTopPriority || isDemos);
      return acc;
    }, {} as Record<string, boolean>);
  });

  // Track recently used pages (from localStorage)
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('recentPages') || '[]');
    } catch {
      return [];
    }
  });

  // Update recently used when pathname changes
  React.useEffect(() => {
    if (pathname && pathname !== '/') {
      const updated = [pathname, ...recentlyUsed.filter(p => p !== pathname)].slice(0, 3);
      setRecentlyUsed(updated);
      localStorage.setItem('recentPages', JSON.stringify(updated));
    }
  }, [pathname]);

  // Filter links based on user roles
  const visibleLinks = useMemo(() =>
    allLinks.filter(link => link.roles.some(requiredRole => roles?.includes(requiredRole))),
    [roles]
  );

  // Search filtered links
  const searchedLinks = useMemo(() => {
    if (!searchQuery.trim()) return visibleLinks;
    const query = searchQuery.toLowerCase();
    return visibleLinks.filter(link =>
      link.label.toLowerCase().includes(query) ||
      link.group?.toLowerCase().includes(query)
    );
  }, [visibleLinks, searchQuery]);

  // Group links
  const groupedLinks = useMemo(() => {
    const links = searchQuery ? searchedLinks : visibleLinks;
    return links.reduce((acc, link) => {
      const group = link.group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push(link);
      return acc;
    }, {} as Record<string, NavLink[]>);
  }, [searchedLinks, visibleLinks, searchQuery]);

  // Sort groups by priority
  const sortedGroups = useMemo(() => {
    // Get user's primary role (highest in hierarchy)
    const primaryRole = ROLE_HIERARCHY.find(role => roles?.includes(role)) || 'user';
    const rolePriority = ROLE_NAVIGATION_PRIORITY[primaryRole as keyof typeof ROLE_NAVIGATION_PRIORITY];

    return (Object.keys(groupedLinks) as Array<keyof typeof groupConfig>).sort((a, b) => {
      // UI/UX Demos always last
      if (a === 'UI/UX Demos') return 1;
      if (b === 'UI/UX Demos') return -1;

      // Use role-based priority
      const indexA = rolePriority.indexOf(a);
      const indexB = rolePriority.indexOf(b);

      // If group is in role priority list, use that order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is in priority list, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // Fallback to default priority
      const priorityA = groupConfig[a as keyof typeof groupConfig]?.priority || 999;
      const priorityB = groupConfig[b as keyof typeof groupConfig]?.priority || 999;
      return priorityA - priorityB;
    });
  }, [groupedLinks, roles]);

  // Recently used links
  const recentLinks = useMemo(() => {
    return recentlyUsed
      .map(path => visibleLinks.find(link => link.to === path))
      .filter(Boolean) as NavLink[];
  }, [recentlyUsed, visibleLinks]);

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 bg-white dark:bg-dark-surface border-r border-gray-200 dark:border-dark-border z-50 flex flex-col transform lg:translate-x-0 transition-all duration-300',
        collapsed ? 'w-20' : 'w-72',
        open ? 'translate-x-0' : '-translate-x-full',
        'shadow-elev-2'
      )}
    >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-dark-border bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 flex-shrink-0">
        {!collapsed && (
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-sm shadow-elev-1">
                ET
              </div>
              <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                ExpTrax
              </span>
            </div>
            {/* Role indicator */}
            {(() => {
              const primaryRole = ROLE_HIERARCHY.find(role => roles?.includes(role)) || 'user';
              const roleLabels = {
                'superadmin': 'Super Admin',
                'admin': 'Admin',
                'approver': 'Approver',
                'scheduler': 'Scheduler',
                'editor': 'Editor',
                'misViewer': 'Viewer',
                'user': 'User'
              };
              return (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-10">
                  {roleLabels[primaryRole as keyof typeof roleLabels]}
                </span>
              );
            })()}
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "hidden lg:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover transition-colors",
            focusRing,
            hitArea
          )}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Menu size={18} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Search Bar (when expanded) */}
      {!collapsed && (
        <div className="p-3 border-b border-gray-200 dark:border-dark-border flex-shrink-0">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search navigation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                "w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500",
                "placeholder:text-gray-400 dark:placeholder:text-gray-500"
              )}
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {/* Recently Used Section */}
        {!collapsed && !searchQuery && recentLinks.length > 0 && (
          <div className="mb-4">
            <div className="px-4 py-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <Clock size={14} />
                Recently Used
              </div>
            </div>
            {recentLinks.map(link => (
              <IconLink
                key={link.to}
                to={link.to}
                icon={link.icon}
                label={link.label}
                active={link.end ? pathname === link.to : pathname.startsWith(link.to)}
                onClick={onClose}
                collapsed={false}
                end={link.end}
                className={cn(
                  "mx-2 mb-1 rounded-lg transition-all duration-200",
                  (link.end ? pathname === link.to : pathname.startsWith(link.to))
                    ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-elev-1"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-hover"
                )}
              />
            ))}
            <div className="mx-4 my-3 border-t border-gray-200 dark:border-dark-border" />
          </div>
        )}

        {/* Grouped Navigation */}
        {sortedGroups.map(groupName => {
          const links = groupedLinks[groupName];
          const config = groupConfig[groupName as keyof typeof groupConfig];
          const isExpanded = searchQuery || expandedGroups[groupName];

          return (
            <div key={groupName} className="mb-2">
              {/* Group Header */}
              <button
                onClick={() => !collapsed && toggleGroup(groupName)}
                className={cn(
                  "w-full px-4 py-2 flex items-center justify-between",
                  "text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                  "hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors",
                  !collapsed && focusRing,
                  collapsed && "justify-center"
                )}
                aria-expanded={!!isExpanded}
              >
                <div className={cn("flex items-center gap-2", collapsed && "justify-center")}>
                  {config?.icon}
                  {!collapsed && groupName}
                </div>
                {!collapsed && !searchQuery && (
                  <ChevronDown
                    size={14}
                    className={cn(
                      "transition-transform duration-200",
                      isExpanded && "rotate-180"
                    )}
                  />
                )}
              </button>

              {/* Group Links */}
              {isExpanded && (
                <div className="mt-1">
                  {links.map(link => (
                    <IconLink
                      key={link.to}
                      to={link.to}
                      icon={link.icon}
                      label={link.label}
                      active={link.end ? pathname === link.to : pathname.startsWith(link.to)}
                      onClick={onClose}
                      collapsed={collapsed}
                      end={link.end}
                      className={cn(
                        "mx-2 mb-1 rounded-lg transition-all duration-200",
                        (link.end ? pathname === link.to : pathname.startsWith(link.to))
                          ? groupName === 'UI/UX Demos'
                            ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-elev-1"
                            : "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-elev-1"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-hover"
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* No Results */}
        {searchQuery && searchedLinks.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No pages found for "{searchQuery}"
          </div>
        )}
      </nav>

      {/* User Profile Section */}
      {!collapsed && user && (
        <div className="p-4 border-t border-gray-200 dark:border-dark-border bg-gradient-to-r from-primary-50/50 to-secondary-50/50 dark:from-primary-900/10 dark:to-secondary-900/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center text-white font-semibold shadow-elev-1">
              {user.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {user.displayName || user.email?.split('@')[0]}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                {roles?.[0] || 'User'}
              </p>
            </div>
          </div>
        </div>
      )}

      {collapsed && user && (
        <div className="p-2 border-t border-gray-200 dark:border-dark-border flex justify-center flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center text-white font-semibold shadow-elev-1">
            {user.email?.charAt(0).toUpperCase() || 'U'}
          </div>
        </div>
      )}
    </aside>
  );
};

export default AppSidebarEnhanced;
